import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Input } from "../../../ui/input";
import { Switch } from "../../../ui/switch";
import { Search, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Monitor, Database, HardDrive, Cloud, Server } from "lucide-react";
import { SiAmazon, SiKubernetes } from "react-icons/si";
import {
  getGCPVMList,
  getGCPStorageList,
  getGCPDatabaseList,
  getAzureVMList,
  getAzureStorageList,
  getAzureDatabaseList,
  getAWSEC2List,
  getAWSS3List,
  getAWSRDSList,
  updateGCPResourcePublish,
  updateAWSResourcePublish,
  updateAzureResourcePublish,
} from "./data";
import AdminKubernetes from "./AdminKubernetes";
import { DetailsDialog } from "../DetailsDialog";
import TenantLists from "../../TenantLists";
import CloudAccountList from "../../CloudAccountList";
import { FaGoogle } from "react-icons/fa6";
import { VscAzure } from "react-icons/vsc";
import { myLocalStorage } from "../../../../../src/components/StorageHelper";
import CloudResourceAnalytics from "./CloudResourceAnalytics";

const ITEMS_PER_PAGE = 12;

const cloudConfig: Record<string, { icon: any; name: string; fullName: string; label: string }> = {
  gcp: { icon: FaGoogle, name: "GCP", fullName: "Google Cloud Platform", label: "GCP" },
  aws: { icon: SiAmazon, name: "AWS", fullName: "Amazon Web Services", label: "AWS" },
  azure: { icon: VscAzure, name: "Azure", fullName: "Microsoft Azure", label: "Azure" },
};

interface AdminCloudResourcesProps {
  cloudProvider: string;
}

const SkeletonResourceCard = () => (
  <Card className="flex flex-col">
    <CardHeader className="pb-4">
      <div className="flex items-start justify-between gap-2">
        <div className="w-12 h-12 rounded-md bg-muted animate-pulse" />
        <div className="h-5 w-16 rounded-md bg-muted animate-pulse" />
      </div>
    </CardHeader>
    <CardContent className="flex flex-col flex-1">
      <div className="flex-1 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-4 w-3/4 rounded-md bg-muted animate-pulse" />
          </div>
          <div className="h-3 w-full rounded-md bg-muted animate-pulse" />
          <div className="h-3 w-2/3 rounded-md bg-muted animate-pulse mt-2" />
        </div>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <div className="h-3 w-16 rounded-md bg-muted animate-pulse" />
            <div className="h-3 w-20 rounded-md bg-muted animate-pulse" />
          </div>
          <div className="flex justify-between">
            <div className="h-3 w-16 rounded-md bg-muted animate-pulse" />
            <div className="h-3 w-20 rounded-md bg-muted animate-pulse" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="h-3 w-16 rounded-md bg-muted animate-pulse" />
            <div className="w-24 h-2 rounded-full bg-muted animate-pulse" />
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t flex items-center justify-between">
        <div className="h-4 w-20 rounded-md bg-muted animate-pulse" />
        <div className="h-6 w-10 rounded-full bg-muted animate-pulse" />
      </div>
    </CardContent>
  </Card>
);

export default function AdminCloudResources({ cloudProvider }: AdminCloudResourcesProps) {
  const [selectedTenant, setSelectedTenant] = useState("");
  const [selectedResourceType, setSelectedResourceType] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeSearchQuery, setTypeSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [publishedResources, setPublishedResources] = useState<Set<string>>(new Set());
  const [mainTab, setMainTab] = useState<"cloud" | "kubernetes">("cloud");
  const [cloudProviderChanges, setCloudProviderChanges] = useState<any>();
  const [currentResources, setCurrentResources] = useState<any[]>([]);
  const [selectedResource, setSelectedResource] = useState<any>(null);
  const [isLoadingResources, setIsLoadingResources] = useState(false);

  const config = cloudConfig[cloudProvider];
  const CloudIcon = config.icon;

  const getResourceTypes = (cloudProvider: string) => {
    if (cloudProvider === "aws") {
      return [
        { id: "ec2", name: "EC2 Instances", type: "EC2", icon: Server, color: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
        { id: "s3", name: "S3 Buckets", type: "S3", icon: Cloud, color: "bg-orange-500/10 text-orange-600 dark:text-orange-400" },
        { id: "rds", name: "RDS Databases", type: "RDS", icon: Database, color: "bg-green-500/10 text-green-600 dark:text-green-400" },
      ];
    }

    return [
      { id: "vm", name: "Virtual Machines", type: "VIRTUAL_MACHINE", icon: Monitor, color: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
      { id: "storage", name: "Storages", type: "STORAGE_ACCOUNT", icon: HardDrive, color: "bg-orange-500/10 text-orange-600 dark:text-orange-400" },
      { id: "database", name: "Databases", type: "DATABASE", icon: Database, color: "bg-green-500/10 text-green-600 dark:text-green-400" },
    ];
  };

  const getCurrentProjectResources = useCallback(async () => {
    if (cloudProvider === "gcp") {
      const [vmList, storageList, dbList] = await Promise.all([
        getGCPVMList(),
        getGCPStorageList(),
        getGCPDatabaseList(),
      ]);
      return [...vmList, ...storageList, ...dbList];
    } else if (cloudProvider === "aws") {
      const [vmList, storageList, dbList] = await Promise.all([
        getAWSEC2List(),
        getAWSS3List(),
        getAWSRDSList(),
      ]);
      return [...vmList, ...storageList, ...dbList];
    } else {
      const [vmList, storageList, dbList] = await Promise.all([
        getAzureVMList(),
        getAzureStorageList(),
        getAzureDatabaseList(),
      ]);
      return [...vmList, ...storageList, ...dbList];
    }
  }, [cloudProvider, selectedTenant, selectedResourceType, cloudProviderChanges]);

  useEffect(() => {
    const fetchResources = async () => {
      setIsLoadingResources(true);
      try {
        const resources = await getCurrentProjectResources();
        setCurrentResources(resources);
      } catch (error) {
        console.error("Error fetching project resources:", error);
      } finally {
        setIsLoadingResources(false);
      }
    };

    fetchResources();
  }, [cloudProvider, selectedTenant, selectedResourceType, mainTab, cloudProviderChanges]);

  const allResourceTypes = getResourceTypes(cloudProvider);

  const filteredResourceTypes = allResourceTypes.filter((type) =>
    type.name.toLowerCase().includes(typeSearchQuery.toLowerCase())
  );

  const typeFilteredResources = selectedResourceType
    ? currentResources.filter((r) => r.type === selectedResourceType)
    : currentResources;

  const filteredResources = searchQuery
    ? typeFilteredResources.filter(
      (r) =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.region.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : typeFilteredResources;

  const totalPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResources = filteredResources.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleFilterChange = (type: string | null) => {
    setSelectedResourceType(type);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleTogglePublish = async (e: React.MouseEvent, resourceId: string, resource: any, type: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (cloudProvider === "aws") {
      await updateAWSResourcePublish(type, resource)
    }
    else if (cloudProvider === "gcp") {
      await updateGCPResourcePublish(type, resourceId)
    } else if (cloudProvider === "azure") {
      let payload = {
        resourceAccountId: resource.id,
        resourceId: resource.instanceId,
        flag: !resource.publish,
        type: type === "vm" ? "VIRTUAL_MACHINE" : type === "storage" ? "STORAGE_ACCOUNT" : "DATABASE"
      }
      await updateAzureResourcePublish(payload)
    }
    setCurrentResources((prev: any[]) =>
      prev.map((item) =>
        item.id === resource.id
          ? { ...item, publish: !item.publish }
          : item
      )
    );
  };

  const getResourceTypeIcon = (type: string, cloudProvider: string) => {
    const types = getResourceTypes(cloudProvider);
    return types.find((rt) => rt.id === type) || types[0];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-green-500/10 text-green-600 dark:text-green-400";
      case "stopped":
        return "bg-red-500/10 text-red-600 dark:text-red-400";
      case "available":
        return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
      default:
        return "bg-gray-500/10 text-gray-600 dark:text-gray-400";
    }
  };

  const handleTenantChange = (tenant: string) => {
    setSelectedTenant(tenant);
  };

  const handleCloudProvider = (value: any) => {
    setCloudProviderChanges(value);
  };

  useEffect(() => {
    if (cloudProvider === "aws") setSelectedResourceType("ec2")
    else setSelectedResourceType("vm")
  }, [cloudProvider])

  const handleResourceClick = (resource: any) => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const latestSubList = JSON.parse(myLocalStorage.getItem("latestSubList") || "{}");
    const gcpProjectId = myLocalStorage.getItem("gcpSelectedProjectId");
    const accountId = myLocalStorage.getItem("selectedAwsAccount");
    const subscriptionId = latestSubList?.azureSubscriptionId;

    const projectId =
      cloudProvider === "gcp"
        ? gcpProjectId
        : cloudProvider === "azure"
          ? subscriptionId
          : accountId;

    setSelectedResource({
      resource,
      cloudProvider,
      projectId,
      tenantName: latestTenant,
      resourceName: cloudProvider === "aws" ? resource.arn || resource.name : resource.name,
      resourceType: resource.type,
    });
  };

  const handleBackFromAnalytics = () => {
    setSelectedResource(null);
  };

  if (selectedResource) {
    return (
      <CloudResourceAnalytics
        cloudProvider={selectedResource.cloudProvider}
        resourceId={selectedResource.resource.id}
        resourceName={selectedResource.resourceName}
        resourceType={selectedResource.resourceType}
        projectId={selectedResource.projectId}
        tenantName={selectedResource.tenantName}
        resource={selectedResource.resource}
        onBack={handleBackFromAnalytics}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="border-b bg-card">
        <div className="px-6 py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <CloudIcon className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-xl font-semibold">{config.name} Admin Resources</h1>
              <p className="text-sm text-muted-foreground">Manage and publish cloud resources</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div>
              <label className="text-xs font-medium mb-1.5 block text-muted-foreground">Tenant</label>
              <TenantLists onSelectorChange={handleTenantChange} isLabelNeeded={false} />
            </div>

            <div>
              <label className="text-xs font-medium mb-1.5 block text-muted-foreground">
                {cloudProvider === "gcp"
                  ? "Project"
                  : cloudProvider === "azure"
                    ? "Subscription"
                    : "Account"}
              </label>
              <CloudAccountList
                cloudProvider={cloudProvider}
                onSelectorChange={handleCloudProvider}
                isLabelNeeded={false}
                isAdmin={true}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-3 border-b flex gap-2 justify-center">
        <button
          onClick={() => setMainTab("cloud")}
          className={`px-5 py-2.5 rounded-md text-sm font-medium flex items-center gap-2 ${mainTab === "cloud"
            ? "bg-primary text-primary-foreground"
            : "bg-muted/50 text-muted-foreground hover-elevate active-elevate-2"
            }`}
        >
          <CloudIcon className="w-4 h-4" />
          Cloud Resources
        </button>

        <button
          onClick={() => setMainTab("kubernetes")}
          className={`px-5 py-2.5 rounded-md text-sm font-medium flex items-center gap-2 ${mainTab === "kubernetes"
            ? "bg-primary text-primary-foreground"
            : "bg-muted/50 text-muted-foreground hover-elevate active-elevate-2"
            }`}
        >
          <SiKubernetes className="w-4 h-4" />
          Kubernetes Resources
        </button>
      </div>

      {mainTab === "cloud" ? (
        <div className="flex-1 flex overflow-hidden">
          <div className="w-64 border-r bg-card flex flex-col flex-shrink-0">
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search types..."
                  value={typeSearchQuery}
                  onChange={(e) => setTypeSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex-1 overflow-auto p-2 space-y-1">
              {filteredResourceTypes.map((type) => {
                const count = currentResources.filter((r) => r.type === type.id).length;
                const Icon = type.icon;
                const isSelected = selectedResourceType === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleFilterChange(type.id)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm ${isSelected ? "bg-primary text-primary-foreground" : "hover-elevate active-elevate-2"
                      }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Icon className="w-4 h-4" />
                      <span className="truncate">{type.name}</span>
                    </div>
                    <Badge variant="secondary">{count}</Badge>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-auto p-6">
            <div className="mb-6 flex items-center justify-between gap-4">
              <div className="flex-1 max-w-md relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search resources by name or region..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{paginatedResources.length}</span> of{" "}
                <span className="font-medium">{filteredResources.length}</span> resources
              </div>
            </div>

            {isLoadingResources ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                  <SkeletonResourceCard key={i} />
                ))}
              </div>
            ) : filteredResources.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <CloudIcon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} resources found</h3>
                  <p className="text-sm text-muted-foreground max-w-md mb-6">
                    You currently don't have any resources for this account.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {paginatedResources.map((resource) => {
                    const resourceTypeInfo = getResourceTypeIcon(resource.type, cloudProvider);
                    const Icon = resourceTypeInfo.icon;

                    return (
                      <Card
                        key={resource.id}
                        onClick={() => handleResourceClick(resource)}
                        className="hover-elevate transition-all flex flex-col cursor-pointer hover:scale-[1.01]"
                      >
                        <CardHeader className="pb-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className={`w-12 h-12 rounded-md ${resourceTypeInfo.color} flex items-center justify-center`}>
                              <Icon className="w-6 h-6" />
                            </div>
                            <Badge variant="secondary" className={`text-xs ${getStatusColor(resource.status)}`}>
                              {resource.status}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="flex flex-col flex-1">
                          <div className="flex-1 space-y-4">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-sm break-all flex-1" title={resource.name}>
                                  {resource.name}
                                </h3>
                                <div onClick={(e) => e.stopPropagation()}>
                                  <DetailsDialog
                                    data={resource}
                                    title={`${resource.name} Details`}
                                    trigger="icon"
                                  />
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2">{resource.description}</p>
                            </div>
                            <div className="space-y-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Region:</span>
                                <span className="font-medium">{resource.region}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Created:</span>
                                <span className="font-medium">{resource.createdAt}</span>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-muted-foreground whitespace-nowrap">Activity:</span>
                                <div className="flex items-center gap-2 flex-1 justify-end">
                                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary rounded-full transition-all duration-300"
                                      style={{ width: `${Math.min(resource.activity || 0, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t flex items-center justify-between">
                            <span className="text-sm font-medium">Published</span>
                            <Switch
                              checked={resource.publish}
                              onCheckedChange={() => { }}
                              onClick={(e) => handleTogglePublish(e, resource.id, resource, resource.type)}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t pt-4">
                    <div className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                        <ChevronsLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) pageNum = i + 1;
                          else if (currentPage <= 3) pageNum = i + 1;
                          else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                          else pageNum = currentPage - 2 + i;

                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              className="w-9"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <AdminKubernetes cloudProvider={cloudProvider} />
      )}
    </div>
  );
}